import { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"

import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { useOnline } from "@/hooks/use-online"
import { cn } from "@/lib/utils"
import type { BulkTransitionEvent, CandidateStatus } from "@/types/candidates"

/**
 * OKS-19 — Sticky-bottom bulk action bar (UX Spec §Flows step 2 +
 * §Responsive). Mounts when the parent's selection set is non-empty
 * (`selectedCount >= 1`); unmounts at zero.
 *
 * **Layout (UX Spec):**
 *   - Sticky bottom on every viewport (no top-anchor variant — that
 *     would push the table down on every selection; CTPO-locked).
 *   - Centered, `min(calc(100vw - 32px), 720px)` width — leaves a
 *     16px gutter at every breakpoint for safe-area + comfortable
 *     reach on mobile.
 *   - 44px minimum interactive elements (touch targets).
 *   - `safe-area-inset-bottom` honored on iOS so the bar floats
 *     above the home-indicator.
 *
 * **Animation (UX Spec §Micro-Interactions):**
 *   - 150ms slide-up on mount via tailwind `animate-in` +
 *     `slide-in-from-bottom-4`.
 *   - `prefers-reduced-motion` → opacity-only via `motion-safe:` /
 *     `motion-reduce:` variants. The animation utilities provide the
 *     reduced-motion fallback automatically; we just don't add any
 *     transform that the reduced-motion media query would have to
 *     suppress.
 *
 * **Focus management (UX Spec §A11y):**
 *   - When `selectedCount` transitions from `0 → ≥1` the `Clear`
 *     button receives focus (first interactive element in the bar's
 *     reading order). Subsequent count changes (`3 → 7`) DO NOT
 *     re-focus, because the user is mid-task and shifting focus
 *     would be disruptive.
 *
 * **State coupling (FE TRD §State & Data Flow):**
 *   - The component is selection-stateless; it consumes
 *     `selectedCount` + callbacks from the parent (`Index.tsx` F6
 *     wires `useBulkSelection` here).
 *   - `submitting` flag (driven by Inertia `processing`) disables
 *     all buttons during in-flight bulk POST.
 *   - `useOnline === false` disables Approve/Reject (the bulk POST
 *     would queue but the user shouldn't think the action took
 *     effect). `Clear` stays enabled — it's purely client-side and
 *     useful regardless of connectivity.
 *
 * **Cap-exceeded defence-in-depth (UX Spec §Edge Cases):**
 *   - `?bulk_skipped=` URL forgery or 26+ ids smuggled directly to
 *     the BE bulk endpoint causes a 303 + `flash[:error]` resolving
 *     to `candidates.flash.bulk.cap_exceeded`. Parent (Index.tsx F6)
 *     reads the flash, passes `capExceeded={true}` through here, and
 *     this component renders the inline `Alert variant="destructive"`
 *     above the bar. **Intentionally NO dedicated testid** (joint
 *     freeze with ux-oks19 + qa-oks19; YAGNI per Phase 2 lock — V1
 *     has no DOM-reachable path to trigger it from happy-path UI).
 *     QA-13 unit-spec stub-flash covers the render path; Playwright
 *     does not exercise it.
 *
 * **Locked testids (FE TRD §testid Map — frozen at TRD publish):**
 *   - `candidate-list-bulk-bar` — sticky bar root
 *   - `candidate-list-bulk-count` — "{n} selected" label (aria-live)
 *   - `candidate-list-bulk-clear` — clear-selection ghost button
 *   - `candidate-list-bulk-approve` — primary bulk approve trigger
 *   - `candidate-list-bulk-reject` — outline bulk reject trigger
 *     (parent owns the modal — F5 BulkRejectConfirmation)
 */
export interface CandidateBulkActionBarProps {
  /** Current selection size (drives mount + count text). */
  selectedCount: number
  /** Callback to clear all selected ids — `useBulkSelection().clear`. */
  onClear: () => void
  /** Callback to submit the bulk approve. Parent owns the Inertia POST. */
  onApprove: () => void
  /**
   * **OKS-950** — the bulk verbs the SERVER says this viewer may use here
   * (`bulk.events`: `SUPPORTED_EVENTS` ∩ actor-permitted ∩ setting-eligible).
   *
   * ⚠️ The forward button is chosen from this, never from the active tab
   * alone. `validate` is brand-member-only, so an agency member on the
   * pending tab gets NO forward button rather than one that would skip every
   * row; and `approve` is absent entirely on a brand routed `always`, which
   * is the widest door to an un-validated approval.
   *
   * Omitted → `["approve", "reject"]`, i.e. the pre-OKS-950 behaviour, so an
   * un-updated caller renders exactly what it rendered before.
   */
  permittedEvents?: BulkTransitionEvent[]
  /**
   * **OKS-950** — the active status tab, which decides WHICH forward verb is
   * meaningful: `validate` on `pending_brand_validation`, `approve`
   * elsewhere. Intersected with {@link permittedEvents}.
   */
  activeStatus?: CandidateStatus | null
  /** **OKS-950** — submit the bulk validate. Parent owns the Inertia POST. */
  onValidate?: () => void
  /**
   * Callback to OPEN the bulk reject confirmation modal. Parent owns
   * the modal state + the submit (F5 BulkRejectConfirmation).
   */
  onReject: () => void
  /**
   * In-flight bulk submission flag. Inertia's `<Form>` render-prop
   * `processing` drives this; while true the buttons disable + show
   * a default loading affordance through the `disabled` attribute.
   */
  submitting?: boolean
  /**
   * Cap-exceeded defence-in-depth flag (BE flash error
   * `code === "too_many"`). Parent (Index.tsx) reads
   * `flash[:error]` and forwards. Renders inline destructive alert
   * above the bar. UX Spec §Edge Cases.
   */
  capExceeded?: boolean
}

export function CandidateBulkActionBar({
  selectedCount,
  onClear,
  onApprove,
  onReject,
  permittedEvents = ["approve", "reject"],
  activeStatus = null,
  onValidate,
  submitting = false,
  capExceeded = false,
}: CandidateBulkActionBarProps) {
  const { t } = useTranslation()
  const online = useOnline()
  const clearRef = useRef<HTMLButtonElement | null>(null)
  const wasMounted = useRef(false)

  // Focus-on-mount semantics: focus moves to `Clear` when selection
  // transitions 0 → ≥1. Track via `wasMounted` ref so subsequent
  // count changes (3 → 7 etc.) don't re-focus and disrupt the user
  // mid-action. When selection drops back to 0 we reset the flag so
  // a future mount cycle re-focuses.
  useEffect(() => {
    if (selectedCount > 0 && !wasMounted.current) {
      wasMounted.current = true
      clearRef.current?.focus()
      return
    }
    if (selectedCount === 0) {
      wasMounted.current = false
    }
  }, [selectedCount])

  // Hard mount gate — bar is DOM-absent at zero selection.
  if (selectedCount === 0) {
    return null
  }

  const submitDisabled = submitting || !online

  // OKS-950 — the forward verb: tab decides WHICH, the server decides WHETHER.
  //
  // ⚠️ Both halves are required. Tab alone would offer Validate to an agency
  // member (every row skips); `permittedEvents` alone would offer Approve on
  // the pending tab, where those rows are past approval. Neither check is
  // redundant, and dropping either produces a button that looks correct and
  // does nothing.
  const onPendingTab = activeStatus === "pending_brand_validation"
  const showValidate = onPendingTab && permittedEvents.includes("validate")
  const showApprove = !onPendingTab && permittedEvents.includes("approve")

  return (
    <>
      {capExceeded && (
        <Alert
          variant="destructive"
          role="alert"
          className={cn(
            // Position above the bar, not on top of it.
            "fixed bottom-20 left-1/2 z-50 -translate-x-1/2",
            "w-[min(calc(100vw-32px),720px)]",
          )}
        >
          <AlertDescription>
            {t("candidates.list.bulk.cap_exceeded")}
          </AlertDescription>
        </Alert>
      )}
      <div
        data-testid="candidate-list-bulk-bar"
        role="region"
        aria-label={t("candidates.list.bulk.bar_aria")}
        className={cn(
          "fixed bottom-0 left-1/2 z-40 -translate-x-1/2",
          "w-[min(calc(100vw-32px),720px)]",
          "bg-background rounded-t-md border border-b-0 shadow-lg",
          "px-4 py-3",
          "flex items-center justify-between gap-3",
          // iOS safe-area: pad the bottom so the bar floats above the
          // home indicator. The `max()` keeps a sensible floor when
          // the env var isn't set.
          "pb-[max(0.75rem,env(safe-area-inset-bottom))]",
          // 150ms slide-up on mount. tailwindcss-animate's
          // `animate-in` + `slide-in-from-bottom-4` already respects
          // `prefers-reduced-motion` automatically (the plugin
          // suppresses the transform when reduced-motion is set,
          // leaving just the fade-in).
          "animate-in slide-in-from-bottom-4 fade-in duration-150",
        )}
      >
        <div className="flex items-center gap-3">
          <span
            data-testid="candidate-list-bulk-count"
            // aria-live="polite" announces count changes to screen
            // readers without preemption. UX Spec §A11y notes a
            // 200ms debounce; sonner-style aggressive announcement
            // is the wrong fit. Polite + atomic re-reads the whole
            // text on change so "12 selected" → "13 selected" lands
            // as a single utterance.
            aria-live="polite"
            aria-atomic="true"
            className="text-sm font-medium tabular-nums"
          >
            {t("candidates.list.bulk.count", { count: selectedCount })}
          </span>
          <Button
            ref={clearRef}
            type="button"
            variant="ghost"
            size="sm"
            data-testid="candidate-list-bulk-clear"
            onClick={onClear}
            // `Clear` stays enabled even when offline — it's purely
            // client-side state and useful for cancelling out before
            // reconnecting. Only `submitting` blocks it (don't let
            // the user clear mid-POST and confuse the result).
            disabled={submitting}
          >
            {t("candidates.list.bulk.clear")}
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="candidate-list-bulk-reject"
            onClick={onReject}
            disabled={submitDisabled}
          >
            {t("candidates.list.bulk.reject")}
          </Button>
          {/*
            OKS-950 — ONE forward verb, chosen by tab ∩ server permission.

            On the `pending_brand_validation` tab the meaningful forward move
            is Validate, not Approve — those rows are past approval and are
            waiting on the brand. Everywhere else it stays Approve.

            Both are then gated on `permittedEvents`, so:
              • an agency member on the pending tab gets NO forward button,
                rather than a Validate that would skip all 25 rows;
              • a brand routed `always` loses bulk Approve entirely.

            No confirmation on either — parity with the shipped bulk approve,
            and the reachable batch is one page of 25 (PRD OQ2).

            The shipped `candidate-list-bulk-approve` testid is untouched on
            every tab where Approve still renders, so the Playwright specs
            bound to it keep working.
          */}
          {showValidate ? (
            <Button
              type="button"
              size="sm"
              data-testid="candidate-list-bulk-validate"
              onClick={onValidate}
              disabled={submitDisabled}
            >
              {t("candidates.list.bulk.validate")}
            </Button>
          ) : (
            showApprove && (
              <Button
                type="button"
                size="sm"
                data-testid="candidate-list-bulk-approve"
                onClick={onApprove}
                disabled={submitDisabled}
              >
                {t("candidates.list.bulk.approve")}
              </Button>
            )
          )}
        </div>
      </div>
    </>
  )
}
